import { useNavigate } from '@tanstack/react-router'
import { Command } from 'cmdk'
import { useEffect, useMemo, useState } from 'react'
import { isOpenStatus, istTeam, TASK_STATUS_LABELS, type TaskStatus } from '@netcon-work/shared'
import { navigationFuer } from '../lib/navigation'
import { useQuery } from '@tanstack/react-query'
import { useAbpinnen, usePinnen } from '../lib/mutationen'
import { schirmQuery, sitzungQuery, workspacesQuery } from '../lib/queries'
import { usePalette } from './PaletteContext'
import { useAufgabeOeffnen, useOffeneAufgabe } from './AufgabeKontext'
import { SourceBadge } from './ui/primitives'
import { useAufgabenAllerWelten } from '../lib/welt'
import { useMerker } from '../lib/merker'
import { aktiverWorkspaceIst } from '../lib/trpc'
import { AufgabenWeltMarke } from './WorkspaceMarke'
import { NotizbuchBefehle } from '../notizbuch'

/** Wo gesucht wird – gemerkt je Gerät wie die Filter der Listen (Z-20). */
const UMFAENGE = ['alle', 'diese'] as const
type Umfang = (typeof UMFAENGE)[number]

/**
 * **Ab wann nach Aufgaben gesucht wird.** Ein einzelner Buchstabe trifft bei 1.847 Aufgaben
 * fast alles – die Liste wäre dann kein Ergebnis, sondern der Bestand in zufälliger Reihenfolge.
 */
const MIND_ZEICHEN = 2

/**
 * **Wie viele Treffer die Palette zeigt.** Sie ist der schnelle Weg zu *einer* bekannten
 * Aufgabe, nicht die Trefferliste – dafür gibt es „Alle Aufgaben". Wenn es mehr sind, sagt die
 * Palette das und schickt weiter, statt still abzuschneiden.
 */
const HOECHSTENS = 12

/**
 * ⌘K-Palette als zweite Navigation (Leitplanke 3: alles per Tastatur).
 *
 * Bewusst von Anfang an dabei, nicht nachträglich – wenn Aktionen erst später
 * ergänzt werden, fehlen sie dauerhaft an der Hälfte der Stellen.
 *
 * ## Sie sucht jetzt auch Aufgaben (21.08.2026)
 *
 * Nils: *„Die Suche oben in Work – die durchsucht gar nicht die Aufgaben, die durchsucht nur
 * die Menüpunkte."* Er hatte recht, und das Feld versprach mit „… oder suchen" etwas, das es
 * nicht gab. Aufgaben waren nur in „Alle Aufgaben" zu finden – wer im Eingang stand und eine
 * bestimmte Ticketnummer suchte, musste die Ansicht wechseln.
 *
 * Der Bestand liegt ohnehin im Client (Leitplanke 2), also kostet die Suche keine Abfrage:
 * geladen wird er erst beim ersten Öffnen der Palette (`enabled`), danach hält ihn der Cache.
 * Ein Gast bekommt vom Server nur seine eigenen Aufgaben – die Palette braucht dafür keine
 * eigene Regel.
 *
 * ## Warum die Palette selbst filtert (`shouldFilter={false}`)
 *
 * cmdk bewertet sonst jeden Eintrag unscharf und **verwirft** dabei Zeilen, die wir gerade
 * bewusst ausgewählt haben – eine Ticketnummer, die nur im Nebenfeld steht, fiele wieder
 * heraus. Zwei Filter hintereinander sind einer zu viel; welcher gewinnt, sieht man dem
 * Ergebnis nicht an.
 */
export function CommandPalette() {
  const { offen, schliessen, umschalten } = usePalette()
  const navigate = useNavigate()
  /**
   * **Dieselbe Liste wie die Seitenleiste** (Phase 9). Die Palette ist die zweite Navigation
   * (Leitplanke 3) – zwei Navigationen mit unterschiedlichem Inhalt wären die zuverlässigste
   * Art, jemanden in eine Ansicht zu schicken, die ihn abweist.
   */
  const { data: person } = useQuery(sitzungQuery)
  const eintraege = useMemo(() => navigationFuer(person?.role ?? 'entwickler'), [person?.role])
  /**
   * **Die Suche geht über alle meine Arbeitswelten** ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md),
   * 05.10.2026, Ausnahme von [E-234](../../../../docs/ENTSCHEIDUNGEN.md#e-234)).
   *
   * Bis hierher war sie gebunden: *„Wer in Marketing sucht, soll nicht auf Entwicklungsaufgaben
   * stoßen, die er von dort aus nicht öffnen kann."* Der zweite Halbsatz stimmt längst nicht
   * mehr – das Detail öffnet jede Aufgabe in **ihrer** Welt, ohne umzuschalten. Und der erste
   * war genau das Hindernis: Nils, 05.10.2026, *„Die Allgemeine Suche soll alle Workspaces
   * durchsuchen können."* Wer sucht, weiß oft nicht, wo etwas liegt.
   *
   * **Die aktive Welt steht vorn**, fremde Treffer tragen ihre Marke. Wer eingrenzen will, drückt
   * `Tab` oder den Schalter – gemerkt je Gerät. Kein Serveraufruf: die Aufgaben aller eigenen
   * Welten lagen schon im Browser, die Palette hatte sie nur ausgeblendet.
   */
  const { aufgaben: alleAufgaben, laedt: bestandLaedt } = useAufgabenAllerWelten()
  const { data: workspaces = [] } = useQuery(workspacesQuery)
  const mehrereWelten = workspaces.length >= 2
  const [umfang, setUmfang] = useMerker<Umfang>('palette-umfang', 'alle', UMFAENGE)
  const aktiveWelt = aktiverWorkspaceIst()
  const aktiveWeltName = workspaces.find((w) => w.id === aktiveWelt)?.label ?? 'diese Welt'
  const nurDiese = mehrereWelten && umfang === 'diese' && !!aktiveWelt
  const aufgaben = useMemo(
    () => (nurDiese ? alleAufgaben.filter((a) => a.workspaceId === aktiveWelt) : alleAufgaben),
    [alleAufgaben, nurDiese, aktiveWelt],
  )
  const detailOeffnen = useAufgabeOeffnen()

  /* ── Anpinnen (Z-03) – gilt der gerade offenen Aufgabe ─────────────────── */
  const offeneAufgabe = useOffeneAufgabe()
  const imTeam = person ? istTeam(person.role) : false
  const { data: schirm } = useQuery({ ...schirmQuery, enabled: offen && imTeam })
  const pinnen = usePinnen()
  const abpinnen = useAbpinnen()
  const istGepinnt = Boolean(
    offeneAufgabe &&
    schirm?.pins.some(
      (p) => p.art === 'gepinnt' && p.ziel.art === 'aufgabe' && p.ziel.taskId === offeneAufgabe,
    ),
  )

  const [suche, setSuche] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        umschalten()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [umschalten])

  const begriff = suche.trim().toLowerCase()

  const ansichten = useMemo(
    () => (begriff ? eintraege.filter((e) => e.label.toLowerCase().includes(begriff)) : eintraege),
    [begriff, eintraege],
  )

  const treffer = useMemo(() => {
    if (begriff.length < MIND_ZEICHEN) return { zeilen: [], gesamt: 0, anderswo: 0 }
    const passend = aufgaben.filter(
      (a) =>
        a.title.toLowerCase().includes(begriff) ||
        /**
         * **Alle Kennungen, nicht nur die führende** – wie in „Alle Aufgaben". Eine Aufgabe
         * aus GitLab mit NetCon-Ticket zeigt `GitLab !809`; wer nach `93551` sucht, meint
         * dieselbe Aufgabe.
         */
        (a.alleKennungen?.toLowerCase().includes(begriff) ?? false) ||
        (a.externalKey?.toLowerCase().includes(begriff) ?? false) ||
        (a.assigneeName?.toLowerCase().includes(begriff) ?? false),
    )
    /**
     * **Offenes zuerst, dann das zuletzt Geänderte.** Über tausend der Aufgaben sind erledigt;
     * ohne diese Reihenfolge stünden bei jedem zweiten Suchwort zwölf Leichen vorn und die
     * eine offene Aufgabe, die gemeint war, auf Platz dreizehn – also unsichtbar.
     */
    const sortiert = [...passend].sort((a, b) => {
      const offenA = isOpenStatus(a.status)
      const offenB = isOpenStatus(b.status)
      if (offenA !== offenB) return offenA ? -1 : 1
      /**
       * **Z-20: erst offen, dann die aktive Welt** (05.10.2026, im Browser gefunden). Stand die
       * Welt vorn, füllten bei „Solingen" sieben erledigte Entwicklungsaufgaben die zwölf Plätze,
       * und die offene, heute fällige aus „Meine ToDos" fiel hinten heraus.
       */
      const hierA = a.workspaceId === aktiveWelt
      const hierB = b.workspaceId === aktiveWelt
      if (hierA !== hierB) return hierA ? -1 : 1
      return zeitpunkt(b.updatedAt) - zeitpunkt(a.updatedAt)
    })
    /** Was in anderen Welten läge, wenn eingegrenzt ist – für den Hinweis „3 weitere". */
    const anderswo = nurDiese
      ? alleAufgaben.filter(
          (a) =>
            a.workspaceId !== aktiveWelt &&
            (a.title.toLowerCase().includes(begriff) ||
              (a.alleKennungen?.toLowerCase().includes(begriff) ?? false) ||
              (a.externalKey?.toLowerCase().includes(begriff) ?? false)),
        ).length
      : 0
    return { zeilen: sortiert.slice(0, HOECHSTENS), gesamt: sortiert.length, anderswo }
  }, [aufgaben, alleAufgaben, begriff, aktiveWelt, nurDiese])

  /** Die Suche fängt bei jedem Öffnen neu an – ein alter Begriff wäre ein Filter, den niemand gesetzt hat. */
  function zu() {
    setSuche('')
    schliessen()
  }

  return (
    <Command.Dialog
      open={offen}
      onOpenChange={(o) => (o ? undefined : zu())}
      label="Befehlspalette"
      shouldFilter={false}
      className="fixed inset-x-0 bottom-0 unter-leiste z-100 flex items-start justify-center bg-ink/45 p-3 pt-[8vh] sm:p-5 sm:pt-[15vh]"
      /**
       * **Ein Klick daneben schließt** (NCW-6X848M: *„Suche oben schließt sich nicht wenn man
       * rausclickt"*, [E-309](../../../docs/ENTSCHEIDUNGEN.md#e-309)).
       *
       * Die Ursache steckt eine Zeile höher: der graue Hintergrund ist keine Hülle **um** den
       * Dialog, sondern der Dialog **selbst** (`fixed inset-x-0 bottom-0 unter-leiste … bg-ink/45` steht an
       * `Command.Dialog`). Damit liegt jeder Klick auf der Fläche technisch **innerhalb** – und
       * „außerhalb", worauf die eingebaute Schließen-Erkennung wartet, gibt es nicht.
       *
       * Ein `onClick` an der Fläche wäre die falsche Reparatur: er feuerte auch, wenn eine
       * Auswahl im Inneren beginnt und der Mauszeiger beim Loslassen über dem Rand landet. Der
       * Zeigerdruck **mit Zielprüfung** ist die Bewegung, die ein Mensch als „daneben" meint.
       */
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) zu()
      }}
    >
      <div className="w-full min-w-0 max-w-[540px] overflow-hidden rounded-2xl bg-surface shadow-[0_20px_60px_rgb(0_0_0/0.3)]">
        {/**
         * **Auf dem Telefon der Schalter unter dem Feld** (06.10.2026): daneben nahm er mehr als
         * die Hälfte der Zeile, und für den Suchbegriff blieben 130 px.
         */}
        <div className="flex flex-wrap items-center gap-x-2 border-b border-line pr-3 max-sm:pr-0 max-sm:pb-2.5">
          <Command.Input
            value={suche}
            onValueChange={setSuche}
            placeholder="Springen zu … oder suchen"
            onKeyDown={(e) => {
              /** `Tab` grenzt ein oder weitet aus – die Hand bleibt auf der Tastatur (Leitplanke 3). */
              if (e.key === 'Tab' && mehrereWelten && !e.shiftKey) {
                e.preventDefault()
                setUmfang(umfang === 'alle' ? 'diese' : 'alle')
              }
            }}
            className="min-w-0 flex-1 px-[18px] py-4 text-[14px] outline-none placeholder:text-muted max-sm:basis-full max-sm:text-[16px]"
          />
          {mehrereWelten && (
            <div
              role="group"
              aria-label="Wo gesucht wird"
              className="inline-flex shrink-0 overflow-hidden rounded-lg border border-line2 text-[11.5px] max-sm:ml-[18px]"
            >
              {UMFAENGE.map((u, i) => (
                <button
                  key={u}
                  type="button"
                  aria-pressed={umfang === u}
                  onClick={() => setUmfang(u)}
                  className={`cursor-pointer px-2 py-[3px] font-semibold ${i > 0 ? 'border-l border-line2' : ''} ${
                    umfang === u
                      ? 'bg-accent-soft text-accent-deep'
                      : 'text-ink2 hover:bg-[#f3f2ef]'
                  }`}
                >
                  {u === 'alle' ? 'Alle Welten' : `Nur ${aktiveWeltName}`}
                </button>
              ))}
            </div>
          )}
        </div>
        <Command.List className="max-h-[320px] overflow-auto p-2">
          {/**
           * **Der leere Zustand steht hier von Hand und nicht in `Command.Empty`.** cmdk zählt
           * dafür seine eigenen Einträge – und seit die Palette selbst filtert
           * (`shouldFilter={false}`), stünde „Nichts gefunden." auch dann da, wenn darunter
           * gerade „Der Bestand wird noch geladen …" steht. Zwei Auskünfte, die einander
           * widersprechen, sind schlechter als eine.
           */}
          {ansichten.length === 0 && treffer.zeilen.length === 0 && !bestandLaedt && (
            <p className="px-3 py-6 text-center text-[13px] text-muted">Nichts gefunden.</p>
          )}

          {/**
           * **Anpinnen per Tastatur** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md),
           * Leitplanke 3).
           *
           * Der Befehl gilt dem, was **gerade offen** ist – deshalb steht er nur da, wenn ein
           * Detailfenster im Bild ist, und dann ganz oben: er ist in diesem Moment die
           * naheliegendste Handlung, und die Reihenfolge entscheidet, was die Eingabetaste
           * trifft.
           *
           * Der zweite Weg ist die Taste `p` am Fenster selbst (`PinKnopf`); dieser hier ist für
           * alle, die ohnehin mit ⌘K arbeiten.
           */}
          {offeneAufgabe && imTeam && (
            <Command.Group heading="Diese Aufgabe" className={GRUPPE}>
              <Command.Item
                value={`pin:${offeneAufgabe}`}
                onSelect={() => {
                  zu()
                  const ziel = { art: 'aufgabe' as const, taskId: offeneAufgabe }
                  if (istGepinnt) abpinnen.mutate(ziel)
                  else pinnen.mutate(ziel)
                }}
                className={`${ZEILE} items-center`}
              >
                <span aria-hidden>📌</span>
                {istGepinnt ? 'Abpinnen' : 'Anpinnen – auf den Schirm legen'}
              </Command.Item>
            </Command.Group>
          )}

          {ansichten.length > 0 && (
            <Command.Group heading="Ansichten" className={GRUPPE}>
              {ansichten.map((eintrag) => {
                const Icon = eintrag.icon
                return (
                  <Command.Item
                    key={eintrag.id}
                    value={`ansicht:${eintrag.id}`}
                    onSelect={() => {
                      zu()
                      void navigate({ to: eintrag.path })
                    }}
                    className={`${ZEILE} items-center`}
                  >
                    <Icon className="size-[17px] opacity-75" />
                    {eintrag.label}
                  </Command.Item>
                )
              })}
            </Command.Group>
          )}

          {/* Das Notizbuch bringt seine Befehle selbst mit (E-419) – nur mit Häkchen. */}
          <NotizbuchBefehle zu={zu} gruppe={GRUPPE} zeile={`${ZEILE} items-center`} />

          {/**
           * **Aufgaben in derselben Liste, aber als eigene Gruppe.** Die Ansichten bleiben
           * oben: wer „Leitstand" tippt, will die Ansicht, nicht die Aufgabe, in deren Titel
           * das Wort vorkommt. Die Reihenfolge entscheidet, was die Eingabetaste trifft.
           */}
          {treffer.zeilen.length > 0 && (
            <Command.Group
              heading={
                treffer.gesamt > treffer.zeilen.length
                  ? `Aufgaben (${treffer.zeilen.length} von ${treffer.gesamt})`
                  : `Aufgaben (${treffer.gesamt})`
              }
              className={GRUPPE}
            >
              {treffer.zeilen.map((a) => (
                <Command.Item
                  key={a.id}
                  value={`aufgabe:${a.id}`}
                  onSelect={() => {
                    zu()
                    detailOeffnen(a.id)
                  }}
                  className={`${ZEILE} items-start`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{a.title}</span>
                    <span className="mt-[3px] flex items-center gap-1.5 text-[11px] text-muted">
                      <SourceBadge source={a.sourceId} label={a.externalKey ?? a.sourceLabel} />
                      <span className={isOpenStatus(a.status) ? '' : 'opacity-60'}>
                        {TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status}
                      </span>
                      {a.assigneeName && <span className="truncate">· {a.assigneeName}</span>}
                      {/** Fremde Welt: die Marke sagt, wo die Aufgabe liegt (Z-20). */}
                      <AufgabenWeltMarke
                        workspaceId={a.workspaceId}
                        zeigen={a.workspaceId !== aktiveWelt}
                        className="ml-auto shrink-0"
                      />
                    </span>
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/**
           * **Der Hinweis, wenn die Suche noch zu kurz ist.** Ohne ihn sieht die Palette bei
           * einem Buchstaben so aus, als kenne sie keine Aufgaben – genau der Eindruck, der
           * sie vorher wertlos machte.
           */}
          {begriff.length > 0 && begriff.length < MIND_ZEICHEN && (
            <p className="px-3 py-2 text-[11.5px] text-muted">
              Noch ein Zeichen, dann wird auch in den Aufgaben gesucht.
            </p>
          )}
          {/**
           * **Beim allerersten Öffnen kann der Bestand noch unterwegs sein.** `tasks.all` ist
           * die schwerste Abfrage der Anwendung; ohne diese Zeile sähe die Sekunde davor aus
           * wie „gibt es nicht" – die Antwort, die man sich merkt.
           */}
          {begriff.length >= MIND_ZEICHEN && bestandLaedt && (
            <p className="px-3 py-2 text-[11.5px] text-muted">Der Bestand wird noch geladen …</p>
          )}
          {begriff.length >= MIND_ZEICHEN && !bestandLaedt && treffer.gesamt === 0 && (
            <p className="px-3 py-2 text-[11.5px] text-muted">
              {nurDiese
                ? `Keine Aufgabe dazu in ${aktiveWeltName}.`
                : 'Keine Aufgabe dazu gefunden.'}
            </p>
          )}
          {treffer.anderswo > 0 && (
            <p className="px-3 py-2 text-[11.5px] text-muted">
              {treffer.anderswo} weitere in anderen Welten
              {/** Auf dem Telefon gibt es keine Tab-Taste – dort ist es der Schalter oben. */}
              <span className="max-sm:hidden">
                {' '}
                – <kbd className="rounded border border-line2 px-1.5 py-px">Tab</kbd> zeigt sie.
              </span>
              <span className="sm:hidden"> – „Alle Welten" oben zeigt sie.</span>
            </p>
          )}
        </Command.List>
        <div className="border-t border-line px-[18px] py-2.5 text-[11px] text-muted">
          {begriff.length >= MIND_ZEICHEN ? (
            <>
              Aufgaben: Titel, Ticketnummer und Bearbeiter · alle Stati ·{' '}
              {nurDiese ? `nur ${aktiveWeltName}` : 'alle Arbeitswelten'}
              {mehrereWelten && (
                <span className="max-sm:hidden">
                  {' '}
                  · <kbd className="rounded border border-line2 px-1.5 py-px">Tab</kbd> wechselt
                </span>
              )}
            </>
          ) : (
            <>
              <kbd className="rounded border border-line2 px-1.5 py-px">↑↓</kbd> wählen ·{' '}
              <kbd className="rounded border border-line2 px-1.5 py-px">↵</kbd> öffnen ·{' '}
              <kbd className="rounded border border-line2 px-1.5 py-px">Esc</kbd> schließen
            </>
          )}
        </div>
      </div>
    </Command.Dialog>
  )
}

/** Ein fehlender Zeitstempel sortiert nach hinten, nicht auf 1970 – sonst führt er die Liste an. */
function zeitpunkt(wert: string | Date | null | undefined): number {
  if (!wert) return 0
  const zahl = new Date(wert).getTime()
  return Number.isNaN(zahl) ? 0 : zahl
}

const GRUPPE =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.5px] [&_[cmdk-group-heading]]:text-muted [&_[cmdk-group-heading]]:uppercase'

const ZEILE =
  'flex cursor-pointer gap-2.5 rounded-[9px] px-3 py-2.5 text-[13.5px] data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent-deep'
